import React from 'react';
import { ExternalLink, CheckCircle, Award, Shield } from 'lucide-react';

export const Legitimacy = () => {
  return (
    <section className="py-16 bg-gradient-to-r from-blue-500 to-blue-600">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Certifications */}
          <div>
            <h3 className="text-3xl font-bold text-white mb-6">Officially Recognized & Certified</h3>
            <div className="space-y-4">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6 hover:bg-white/20 transition-all">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                      <Shield className="w-7 h-7 text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-lg mb-2">Ontario Ministry of Education</h4>
                    <p className="text-blue-100 text-sm mb-3">Our OSSD program is inspected and approved by the Ontario Ministry of Education</p>
                    <a 
                      href="http://www.edu.gov.on.ca/eng/document/curricul/secondary/descript/descri9e.pdf" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-amber-300 hover:text-amber-200 font-semibold text-sm transition-colors"
                    >
                      View OSSD Requirements
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6 hover:bg-white/20 transition-all">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                      <Award className="w-7 h-7 text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-lg mb-2">OECD PISA Recognition</h4>
                    <p className="text-blue-100 text-sm mb-3">Ontario consistently ranks in TOP 5 globally for education quality</p>
                    <a 
                      href="https://www.oecd.org/pisa/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-amber-300 hover:text-amber-200 font-semibold text-sm transition-colors"
                    >
                      View PISA Rankings
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6 hover:bg-white/20 transition-all">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-7 h-7 text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-lg mb-2">Ontario Curriculum Standards</h4>
                    <p className="text-blue-100 text-sm mb-3">All courses follow official Ontario curriculum guidelines</p>
                    <a 
                      href="http://www.edu.gov.on.ca/eng/curriculum/secondary/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-amber-300 hover:text-amber-200 font-semibold text-sm transition-colors"
                    >
                      View Curriculum
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Trust Indicators */}
          <div className="bg-white rounded-3xl p-10 shadow-2xl">
            <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">Why Choose EduNexa?</h3>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-lg mb-1">1. Partnered With an Official OSSD School</h4>
                  <p className="text-gray-600 leading-relaxed">We collaborate with a fully inspected and Ministry-approved Ontario private school that is authorized to deliver OSSD programs. EduNexa facilitates student enrollment and support — the diploma is issued by the partnering Ontario school.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-lg mb-1">2. Ontario-Certified Teachers (OCT)</h4>
                  <p className="text-gray-600 leading-relaxed">All coursework is taught and evaluated by Ontario Certified Teachers (OCT) through our partnered school, ensuring students receive authentic Canadian academic standards and high-quality instruction.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-lg mb-1">3. Ministry-Regulated Curriculum</h4>
                  <p className="text-gray-600 leading-relaxed">The OSSD program follows the official Ontario curriculum, with regular inspections and quality assurance by the Ontario Ministry of Education through our partner institution.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-lg mb-1">4. Proven Student Success</h4>
                  <p className="text-gray-600 leading-relaxed">Thousands of OSSD graduates from our partner school network have progressed to leading universities across Canada, the UK, USA, Australia, Singapore, Hong Kong, Europe, and more. (Admission outcomes vary by student, program, and institution.)</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-lg mb-1">5. Transparent Support Through EduNexa</h4>
                  <p className="text-gray-600 leading-relaxed mb-2">EduNexa provides consistent guidance for students and families, including:</p>
                  <ul className="text-gray-600 text-sm space-y-1 ml-4">
                    <li>• Course selection</li>
                    <li>• Enrollment support</li>
                    <li>• Academic planning</li>
                    <li>• University pathway guidance</li>
                  </ul>
                  <p className="text-gray-600 text-sm mt-2 italic">We serve as your trusted local support, ensuring a smooth and supportive learning journey from start to finish.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-lg mb-1">6. Global Pathway Expertise</h4>
                  <p className="text-gray-600 leading-relaxed">With experience supporting students across Asia, Africa, and the Middle East, EduNexa offers clear, practical guidance on how to use the OSSD for university applications worldwide.</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};
